import { Nullable, ObjectValues } from 'src/app/types/global.types';

// ---- STATUS & EXPORT ----
export type ObjectVal<T> = ObjectValues<T>;

export const StatusPelaporanBupot = {
  REKAP_BERJALAN: 'rekap_berjalan',
  BELUM_LAPOR: 'belum_lapor',
  BERMASALAH: 'bermasalah',
  LAPOR_BUPOT: 'lapor_bupot',
  TIDAK_LAPOR: 'tidak_lapor',
  TIDAK_ADA_TRANSAKSI: 'tidak_ada_transaksi',
} as const;

export const PENDAPATAN_STATUS = StatusPelaporanBupot;

export function parsePeriodeToMonthYear(periode?: string): { year: number; monthName: string } {
  if (!periode) return { year: 0, monthName: '' };
  const match = periode.trim().match(/^([A-Za-z]+)\s+(\d{4})/);
  if (!match) return { year: 0, monthName: '' };

  const monthName = match[1];
  const year = parseInt(match[2], 10) || 0;

  return { year, monthName };
}

export const PENDAPATAN_STATUS_LABEL: Record<string, string> = {
  [PENDAPATAN_STATUS.REKAP_BERJALAN]: 'Rekap Berjalan',
  [PENDAPATAN_STATUS.BELUM_LAPOR]: 'Belum Lapor',
  [PENDAPATAN_STATUS.BERMASALAH]: 'Bermasalah',
  [PENDAPATAN_STATUS.LAPOR_BUPOT]: 'Lapor Bupot',
  [PENDAPATAN_STATUS.TIDAK_LAPOR]: 'Tidak Lapor',
  [PENDAPATAN_STATUS.TIDAK_ADA_TRANSAKSI]: 'Tidak Ada Transaksi',
  // Backward-compatibility keys (uppercase)
  REKAP_BERJALAN: 'Rekap Berjalan',
  BELUM_LAPOR: 'Belum Lapor',
  BERMASALAH: 'Bermasalah',
  LAPOR_BUPOT: 'Lapor Bupot',
  TIDAK_LAPOR: 'Tidak Lapor',
  TIDAK_ADA_TRANSAKSI: 'Tidak Ada Transaksi',
};

export const PENDAPATAN_SORT = {
  TERBARU: 'desc',
  TERLAMA: 'asc',
} as const;

// ---- BACKEND RESOURCE SCHEMAS (/agen-cabang/bupot) ----
export interface BupotAgentResource {
  uuid: string;
  username: string;
  fullname: string;
}

export interface BupotTaxSummaryResource {
  tax_type: string;
  tax_amount: number;
  transaction_count: number;
}

export interface BupotItemResource {
  uuid: string;
  bupot_number: string;
  agent: BupotAgentResource;
  period_start: string;
  period_end: string;
  gross_amount: number;
  tax_amount: number;
  net_amount: number;
  transaction_count: number;
  tax_summary: BupotTaxSummaryResource[];
  status: ObjectValues<typeof PENDAPATAN_STATUS>;
  created_at: string;
  updated_at: string;
}

export interface BupotCounter {
  rekap_berjalan: number;
  belum_lapor: number;
  bermasalah: number;
  lapor_bupot: number;
  tidak_lapor: number;
  tidak_ada_transaksi: number;
}

// ---- OVERVIEW TYPE (Untuk diserahkan ke Backend & dipakai Frontend) ----
export interface BupotOverview {
  total_pendapatan_kotor: number;
  total_potongan_pph23: number;
  total_pendapatan_bersih: number;
  jumlah_laporan: number;
  tahun: number;
}

// Backward-compatible alias
export type PendapatanAgenOverview = BupotOverview;

// ---- ITEM (UI view model per baris table) ----
export interface PendapatanAgenItem {
  uuid: string;
  nomor_pendapatan: string;           // dari bupot_number
  periode_label: string;              // dari period_start (contoh: "Sep 2026")
  periode_range: string;              // dari period_start & period_end (contoh: "(1 - 30)")
  bulan: number;
  tahun: number;
  total_pendapatan_kotor: number;     // dari gross_amount
  potongan_pph23: number;             // potongan pajak (negatif di UI)
  pendapatan_bersih: number;          // dari net_amount
  status: ObjectValues<typeof PENDAPATAN_STATUS>;
  tanggal_diperbarui: string;         // dari updated_at
  agent?: BupotAgentResource;
  transaction_count?: number;
  tax_summary?: BupotTaxSummaryResource[];
}

// ---- API RESPONSE ----
export interface GetBupotListResponse {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
  counter: BupotCounter;
  data: BupotItemResource[];
  overview?: BupotOverview;
}

// Backward-compatible alias
export type GetPendapatanAgenListResponse = GetBupotListResponse;

// ---- API QUERY ----
export interface GetPendapatanAgenQuery {
  page: number;
  per_page?: number;
  year?: number;
  tahun?: number;
  search?: string;
  sort?: string;
  status?: string;
}

// Helper transformasi dari response Bupot ke format UI PendapatanAgenItem
export function transformBupotItemToPendapatanItem(item: any): PendapatanAgenItem {
  let periodeLabel = item.periode_label || '';
  let periodeRange = item.periode_range || '';
  let bulan = item.bulan || 1;
  let tahun = item.tahun || new Date().getFullYear();

  if (item.period_start) {
    const startDate = new Date(item.period_start);
    if (!isNaN(startDate.getTime())) {
      bulan = startDate.getMonth() + 1;
      tahun = startDate.getFullYear();

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
      periodeLabel = `${monthNames[startDate.getMonth()]} ${tahun}`;

      const startDay = startDate.getDate();
      let endDay = startDay;
      if (item.period_end) {
        const endDate = new Date(item.period_end);
        if (!isNaN(endDate.getTime())) {
          endDay = endDate.getDate();
        }
      }
      periodeRange = `(${startDay} - ${endDay})`;
    }
  }

  // Sesuai instruksi: potongan pajak sementara diberi negatif semua & warna merah
  const rawTax = item.tax_amount !== undefined ? item.tax_amount : (item.potongan_pph23 !== undefined ? item.potongan_pph23 : 0);
  const potonganPph = rawTax !== 0 ? -Math.abs(rawTax) : 0;

  return {
    uuid: item.uuid,
    nomor_pendapatan: item.bupot_number || item.nomor_pendapatan || '',
    periode_label: periodeLabel,
    periode_range: periodeRange,
    bulan,
    tahun,
    total_pendapatan_kotor: item.gross_amount !== undefined ? item.gross_amount : (item.total_pendapatan_kotor ?? 0),
    potongan_pph23: potonganPph,
    pendapatan_bersih: item.net_amount !== undefined ? item.net_amount : (item.pendapatan_bersih ?? 0),
    status: item.status,
    tanggal_diperbarui: item.updated_at || item.tanggal_diperbarui || item.created_at || '',
    agent: item.agent,
    transaction_count: item.transaction_count,
    tax_summary: item.tax_summary,
  };
}

// MOCK DATA GENERATOR
export function MockResponseTravelBusList(query: GetPendapatanAgenQuery): GetPendapatanAgenListResponse {
  const allItems: any[] = [
    {
      uuid: '1ba72455-0c05-45bb-9fbf-5f758007d6b8',
      bupot_number: 'PDN20260901UEFB',
      nomor_pendapatan: 'PDN-2606EDFCD',
      periode_label: 'Okt 2026',
      periode_range: '(1 - 31)',
      period_start: '2026-10-01',
      period_end: '2026-10-31',
      bulan: 10,
      tahun: 2026,
      gross_amount: 10000000,
      tax_amount: 200000,
      net_amount: 9800000,
      total_pendapatan_kotor: 10000000,
      potongan_pph23: -200000,
      pendapatan_bersih: 9800000,
      status: PENDAPATAN_STATUS.BELUM_LAPOR,
      tanggal_diperbarui: '2024-04-10T10:00:00'
    },
    {
      uuid: '2',
      bupot_number: 'PDN20260901UEFC',
      nomor_pendapatan: 'PDN-2606EDFFFF',
      periode_label: 'Sep 2026',
      periode_range: '(1 - 30)',
      period_start: '2026-09-01',
      period_end: '2026-09-30',
      bulan: 9,
      tahun: 2026,
      gross_amount: 60000,
      tax_amount: 1500,
      net_amount: 58500,
      total_pendapatan_kotor: 0,
      potongan_pph23: 0,
      pendapatan_bersih: 0,
      status: PENDAPATAN_STATUS.REKAP_BERJALAN,
      tanggal_diperbarui: '2026-09-18T09:36:52'
    },
    {
      uuid: '3',
      bupot_number: 'PDN20260801SURX',
      nomor_pendapatan: 'PDN-2606EDFFFF',
      periode_label: 'Agu 2026',
      periode_range: '(1 - 31)',
      period_start: '2026-08-01',
      period_end: '2026-08-31',
      bulan: 8,
      tahun: 2026,
      gross_amount: 100000,
      tax_amount: 0,
      net_amount: 99000,
      total_pendapatan_kotor: 100000,
      potongan_pph23: 0,
      pendapatan_bersih: 99000,
      status: PENDAPATAN_STATUS.TIDAK_LAPOR,
      tanggal_diperbarui: '2026-03-16T17:00:00'
    },
    {
      uuid: '4',
      bupot_number: 'PDN20260701KTSZ',
      nomor_pendapatan: 'PDN-2606EDFFFF',
      periode_label: 'Jul 2026',
      periode_range: '(1 - 31)',
      period_start: '2026-07-01',
      period_end: '2026-07-31',
      bulan: 7,
      tahun: 2026,
      gross_amount: 240000,
      tax_amount: 6000,
      net_amount: 234000,
      total_pendapatan_kotor: -200000,
      potongan_pph23: -2000,
      pendapatan_bersih: -198000,
      status: PENDAPATAN_STATUS.TIDAK_ADA_TRANSAKSI,
      tanggal_diperbarui: '2026-03-16T17:00:00'
    },
    {
      uuid: '5',
      bupot_number: 'PDN20260601ABCF',
      nomor_pendapatan: 'PDN-2606EDFCD',
      periode_label: 'Jun 2026',
      periode_range: '(1 - 30)',
      period_start: '2026-06-01',
      period_end: '2026-06-30',
      bulan: 6,
      tahun: 2026,
      gross_amount: 10000000,
      tax_amount: 200000,
      net_amount: 9800000,
      total_pendapatan_kotor: 10000000,
      potongan_pph23: -200000,
      pendapatan_bersih: 9800000,
      status: PENDAPATAN_STATUS.LAPOR_BUPOT,
      tanggal_diperbarui: '2024-05-10T10:00:00'
    },
    {
      uuid: '6',
      bupot_number: 'PDN20260501ABCF',
      nomor_pendapatan: 'PDN-2606EDFCD',
      periode_label: 'Mei 2026',
      periode_range: '(1 - 31)',
      period_start: '2026-05-01',
      period_end: '2026-05-31',
      bulan: 5,
      tahun: 2026,
      gross_amount: 10000000,
      tax_amount: 200000,
      net_amount: 9800000,
      total_pendapatan_kotor: 10000000,
      potongan_pph23: -200000,
      pendapatan_bersih: 9800000,
      status: PENDAPATAN_STATUS.LAPOR_BUPOT,
      tanggal_diperbarui: '2024-05-10T10:00:00'
    },
    {
      uuid: '7',
      bupot_number: 'PDN20260401ABCF',
      nomor_pendapatan: 'PDN-2606EDFCD',
      periode_label: 'Apr 2026',
      periode_range: '(1 - 30)',
      period_start: '2026-04-01',
      period_end: '2026-04-30',
      bulan: 4,
      tahun: 2026,
      gross_amount: 10000000,
      tax_amount: 200000,
      net_amount: 9800000,
      total_pendapatan_kotor: 10000000,
      potongan_pph23: -200000,
      pendapatan_bersih: 9800000,
      status: PENDAPATAN_STATUS.LAPOR_BUPOT,
      tanggal_diperbarui: '2024-04-10T10:00:00'
    },
    {
      uuid: '8',
      bupot_number: 'PDN20260301ABCF',
      nomor_pendapatan: 'PDN-2606EDFFFF',
      periode_label: 'Mar 2026',
      periode_range: '(1 - 31)',
      period_start: '2026-03-01',
      period_end: '2026-03-31',
      bulan: 3,
      tahun: 2026,
      gross_amount: 0,
      tax_amount: 0,
      net_amount: 0,
      total_pendapatan_kotor: 0,
      potongan_pph23: 0,
      pendapatan_bersih: 0,
      status: PENDAPATAN_STATUS.TIDAK_ADA_TRANSAKSI,
      tanggal_diperbarui: '2026-03-16T17:00:00'
    },
    {
      uuid: '9',
      bupot_number: 'PDN20260201ABCF',
      nomor_pendapatan: 'PDN-2606EDFFFF',
      periode_label: 'Feb 2026',
      periode_range: '(1 - 28)',
      period_start: '2026-02-01',
      period_end: '2026-02-28',
      bulan: 2,
      tahun: 2026,
      gross_amount: 0,
      tax_amount: 0,
      net_amount: 0,
      total_pendapatan_kotor: 0,
      potongan_pph23: 0,
      pendapatan_bersih: 0,
      status: PENDAPATAN_STATUS.TIDAK_ADA_TRANSAKSI,
      tanggal_diperbarui: '2026-02-16T17:00:00'
    },
    {
      uuid: '10',
      bupot_number: 'PDN20260101ABCF',
      nomor_pendapatan: 'PDN-2606EDFFFF',
      periode_label: 'Jan 2026',
      periode_range: '(1 - 31)',
      period_start: '2026-01-01',
      period_end: '2026-01-31',
      bulan: 1,
      tahun: 2026,
      gross_amount: 0,
      tax_amount: 0,
      net_amount: 0,
      total_pendapatan_kotor: 0,
      potongan_pph23: 0,
      pendapatan_bersih: 0,
      status: PENDAPATAN_STATUS.TIDAK_ADA_TRANSAKSI,
      tanggal_diperbarui: '2026-01-18T17:00:00'
    }
  ];

  const page = query.page || 1;
  let filteredItems = [...allItems];

  // 1. Filter by Tahun / Year
  const yearFilter = query.year ?? query.tahun;
  if (yearFilter) {
    filteredItems = filteredItems.filter(item => item.tahun === yearFilter);
  }

  // 2. Filter by Status
  if (query.status && query.status !== 'semua') {
    filteredItems = filteredItems.filter(item => item.status === query.status);
  }

  // 3. Filter by Search
  if (query.search) {
    const s = query.search.toLowerCase();
    filteredItems = filteredItems.filter(item => 
      (item.bupot_number && item.bupot_number.toLowerCase().includes(s)) ||
      (item.nomor_pendapatan && item.nomor_pendapatan.toLowerCase().includes(s)) || 
      (item.periode_label && item.periode_label.toLowerCase().includes(s))
    );
  }

  // 4. Sort
  if (query.sort === 'asc' || query.sort === 'terlama') {
    filteredItems.sort((a, b) => a.bulan - b.bulan);
  } else {
    // default/desc
    filteredItems.sort((a, b) => b.bulan - a.bulan);
  }

  const perPage = query.per_page || 10;
  const total = filteredItems.length;
  const lastPage = Math.ceil(total / perPage) || 1;
  const startIndex = (page - 1) * perPage;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + perPage);

  return {
    page: page,
    per_page: perPage,
    last_page: lastPage,
    total: total,
    counter: {
      rekap_berjalan: 1,
      belum_lapor: 1,
      bermasalah: 0,
      lapor_bupot: 3,
      tidak_lapor: 1,
      tidak_ada_transaksi: 4,
    },
    data: paginatedItems,
    overview: {
      total_pendapatan_kotor: 25500000,
      total_potongan_pph23: -510000,
      total_pendapatan_bersih: 24990000,
      jumlah_laporan: 5,
      tahun: yearFilter || 2026
    }
  };
}

// ---- DETAIL BUPOT ACTUAL BACKEND RESPONSE SCHEMAS ----

export interface BupotDetailBreakdownKotor {
  bulan_revenue: number;
  penyesuaian: number;
}

export interface BupotDetailBreakdownPPh {
  bulan_revenue: number;
  penyesuaian: number;
  saldo_kompensasi_pph: number;
}

export interface BupotDetailBreakdownBersih {
  bulan_revenue: number;
  penyesuaian: number;
  saldo_kompensasi_pph: number;
}

export interface BupotDetailOverview {
  bulan_label: string;
  total_pendapatan_kotor: number;
  breakdown_pendapatan_kotor: BupotDetailBreakdownKotor;
  pph_title: string;
  potongan_pph: number;
  breakdown_potongan_pph: BupotDetailBreakdownPPh;
  total_pendapatan_bersih: number;
  breakdown_pendapatan_bersih: BupotDetailBreakdownBersih;
}

export interface BupotRincianBreakdownKotor {
  tot_cb_inbound_booking: Nullable<number>;
  tot_cb_outbound: Nullable<number>;
  tot_cb_ext_inbound_booking: Nullable<number>;
  tot_cb_ext_outbound: Nullable<number>;
  tot_cb_pickup: Nullable<number>;
  tot_cb_dropship: Nullable<number>;
}

export type PendapatanAgenRincianPenyesuaianBreakdown = {
  tot_cb_inbound_booking: Nullable<number>;
  tot_cb_outbound: Nullable<number>;
  tot_cb_ext_inbound_booking: Nullable<number>;
  tot_cb_ext_outbound: Nullable<number>;
  tot_cb_dropship: Nullable<number>;
};

export interface BupotRincianPendapatan {
  jumlah_resi: number;
  berat_total_kg: number;
  total_pendapatan_kotor: number;
  breakdown_kotor: BupotRincianBreakdownKotor;
  pph_label: string;
  potong_pph: number;
  total_pendapatan_bersih: number;
}

export interface BupotRincianPenyesuaian {
  jumlah_resi: number;
  berat_total_kg: number;
  penyesuaian_pendapatan: number;
  breakdown_penyesuaian?: Nullable<PendapatanAgenRincianPenyesuaianBreakdown>;
  pph_label?: string | null;
  potong_pph: number;
  total_penyesuaian: number;
}

export interface BupotDetailTransaksiItem {
  id: string;
  no_trx_mutasi: string;
  tanggal: string;              // "18/9/2026, 16:36"
  no_resi: string;
  tujuan_negara: string;        // "Singapore"
  berat_kg: number;
  inbound_booking: number;
  outbound: number;
  ext_inbound_booking: number;
  ext_outbound: number;
  pick_up: number;
  dropship: number;
  total_pendapatan_kotor: number;
  potongan_pph: number;
  tax_type?: Nullable<string>;
  tax_percentage?: Nullable<number>;
  total_pendapatan_bersih: number;
  tipe_transaksi: string;       // "PICKUP"
  is_pernah_disesuaikan: boolean;
  is_cashback_adjustment: boolean;
}

export interface BupotPelaporanRincian {
  dpp_total_pendapatan_kotor: number;
  ekuivalen_kompensasi: number;
  total_pph: number;
  saldo_kompensasi_pph: number;
  dpp_akhir: number;
  pph_akhir: number;
}

export interface BupotDetailData {
  uuid: string;
  no_pendapatan: string;
  periode: string;              // "September 2026 (1 - 30)"
  nama_agen: string;
  kode_agen: string;
  pelaporan_bupot: ObjectValues<typeof PENDAPATAN_STATUS>;
  tanggal_diperbarui: string;
  pelaporan_rincian: BupotPelaporanRincian;
  overview: BupotDetailOverview;
  saldo_kompensasi_pph: number;
  is_kompensasi_active: boolean;
  rincian_pendapatan: BupotRincianPendapatan;
  rincian_penyesuaian: BupotRincianPenyesuaian;
  transaksi_list: BupotDetailTransaksiItem[];
}

export interface GetBupotDetailResponse {
  message: string;
  data: BupotDetailData;
}

// Backward-compatibility aliases
export type PendapatanAgenDetailData = BupotDetailData;
export type PendapatanAgenTransaksi = BupotDetailTransaksiItem;
export type GetPendapatanAgenDetailResponse = GetBupotDetailResponse;

export interface GetPendapatanAgenDetailQuery {
  uuid: string;
  page?: number;
  search?: string;
  status_trx?: string;
  sort?: string;
}

export function MockResponsePendapatanAgenDetail(query: GetPendapatanAgenDetailQuery): GetBupotDetailResponse {
  const allTransaksi: BupotDetailTransaksiItem[] = [];

  const negaraList = [
    'Singapore',
    'Malaysia',
    'Taiwan',
    'Hong Kong',
    'Japan',
    'South Korea',
    'United States',
  ];

  for (let i = 1; i <= 200; i++) {
    const isPenyesuaian = i % 4 === 0;
    const negara = negaraList[(i - 1) % negaraList.length];
    const day = String(Math.min(28, Math.floor((i - 1) / 1.3) + 1)).padStart(2, '0');
    const hour = String(9 + (i % 12)).padStart(2, '0');
    const minute = String((i * 13) % 60).padStart(2, '0');
    const berat = Number((1.5 + (i * 0.8)).toFixed(1));
    const kotor = isPenyesuaian ? 30000 : 60000 + ((i % 5) * 10000);
    const pph = Math.round(kotor * -0.025);
    const bersih = kotor + pph;

    allTransaksi.push({
      id: String(i),
      no_trx_mutasi: `AXC260918FQF${String(i).padStart(3, '0')}`,
      tanggal: `${day}/9/2026, ${hour}:${minute}`,
      no_resi: `AX2607VIDM${String(i).padStart(3, '0')}`,
      tujuan_negara: negara,
      berat_kg: berat,
      inbound_booking: i % 3 === 0 ? 50000 : 0,
      outbound: i % 5 === 0 ? 35000 : 0,
      ext_inbound_booking: 0,
      ext_outbound: 0,
      pick_up: i % 2 === 0 ? 60000 : 0,
      dropship: i % 2 !== 0 ? 45000 : 0,
      total_pendapatan_kotor: kotor,
      potongan_pph: pph,
      tax_type: 'PPh 21',
      tax_percentage: 2.5,
      total_pendapatan_bersih: bersih,
      tipe_transaksi: isPenyesuaian ? 'PENYESUAIAN' : (i % 2 === 0 ? 'PICKUP' : 'DROPSHIP'),
      is_pernah_disesuaikan: isPenyesuaian,
      is_cashback_adjustment: isPenyesuaian,
    });
  }

  return {
    message: 'Berhasil mengambil data.',
    data: {
      uuid: query.uuid || '1ba72455-0c05-45bb-9fbf-5f758007d6b8',
      no_pendapatan: 'PDN20260901UEFB',
      periode: 'September 2026 (1 - 30)',
      nama_agen: 'AX CABANG DUMMY',
      kode_agen: 'AAXS94CCQPA',
      pelaporan_bupot: PENDAPATAN_STATUS.REKAP_BERJALAN,
      tanggal_diperbarui: '18/9/2026, 16:36',
      pelaporan_rincian: {
        dpp_total_pendapatan_kotor: 250000,
        ekuivalen_kompensasi: 0,
        total_pph: -6250,
        saldo_kompensasi_pph: 0,
        dpp_akhir: 250000,
        pph_akhir: -6250,
      },
      overview: {
        bulan_label: 'Bulan September',
        total_pendapatan_kotor: 60000,
        breakdown_pendapatan_kotor: {
          bulan_revenue: 60000,
          penyesuaian: 0
        },
        pph_title: 'Potongan PPh21 (2.5%)',
        potongan_pph: -1500,
        breakdown_potongan_pph: {
          bulan_revenue: -1500,
          penyesuaian: 0,
          saldo_kompensasi_pph: 0
        },
        total_pendapatan_bersih: 58500,
        breakdown_pendapatan_bersih: {
          bulan_revenue: 58500,
          penyesuaian: 0,
          saldo_kompensasi_pph: 0
        }
      },
      saldo_kompensasi_pph: 750,
      is_kompensasi_active: true,
      rincian_pendapatan: {
        jumlah_resi: 1,
        berat_total_kg: 12,
        total_pendapatan_kotor: 60000,
        breakdown_kotor: {
          tot_cb_inbound_booking: 20000,
          tot_cb_outbound: 20000,
          tot_cb_dropship: 0,
          tot_cb_pickup: 0,
          tot_cb_ext_inbound_booking: 0,
          tot_cb_ext_outbound: 0
        },
        pph_label: 'Potong PPh21 (2.5%)',
        potong_pph: -1500,
        total_pendapatan_bersih: 58500
      },
      rincian_penyesuaian: {
        jumlah_resi: 0,
        berat_total_kg: 0,
        penyesuaian_pendapatan: 20000,
        breakdown_penyesuaian: {
          tot_cb_inbound_booking: null,
          tot_cb_outbound: null,
          tot_cb_ext_inbound_booking: null,
          tot_cb_ext_outbound: null,
          tot_cb_dropship: 0
        },
        pph_label: null,
        potong_pph: 0,
        total_penyesuaian: 20000
      },
      transaksi_list: allTransaksi
    }
  };
}
